import React, { useContext, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import * as R from 'ramda';
import { number, oneOfType, string } from 'prop-types';
import { renderInput } from '../../utils/renderUtils';
import { useGetDestinationsQuery, useGetStatesQuery } from '../../customerAuthorizationsEndpoints';
import SearchResult from './SearchResult';
import { CreateCustomerAuthContext } from '../Create';

const emptyState = { id: '', name: '' };

const renderTags = (tags, getTagProps) =>
  tags.map((item, index) => (
    <Chip
      label={item.name}
      sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
      variant="outlined"
      {...getTagProps({ index })}
    />
  ));

const Destination = ({ idName }) => {
  const [destinationName, setDestinationName] = useState('');
  const [destinationState, setDestinationState] = useState(emptyState);

  const { data: statesQuery = [], isFetching: isFetchingStates } = useGetStatesQuery();

  const {
    info: { controlNumbers, terminals },
  } = useContext(CreateCustomerAuthContext);

  const params = {
    destinationName,
    destinationState: destinationState.name,
    terminalIds: R.pluck('terminalId', terminals),
  };

  const disabled = destinationName.length < 1 || R.equals(destinationState, emptyState);

  const { destinationsQuery = [], isFetchingDestinations } = useGetDestinationsQuery(
    { params },
    {
      selectFromResult: ({ data, isFetching }) => ({
        destinationsQuery: data?.filter(
          destination =>
            !controlNumbers.find(location => destination.destinationIdName === location.locationId)
        ),
        isFetchingDestinations: isFetching,
      }),
      skip: disabled,
    }
  );

  const message = isFetchingDestinations
    ? 'Loading...'
    : `${destinationsQuery.length} Results for "${destinationName}" in ${destinationState.name}`;

  const handleChange = (_, value, reason) => {
    if (reason === 'selectOption') {
      setDestinationState(value);
    }
    if (reason === 'clear') {
      setDestinationState(emptyState);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridGap: 16,
          gridTemplateColumns: '1fr',
          mt: 2,
        }}
      >
        <Typography variant="caption">
          You must select a state and enter a Destination Name to see a list of destinations
        </Typography>
        <Box
          sx={{
            display: 'flex',
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            flexDirection: { xs: 'column', lg: 'row' },
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            gridColumnGap: { xs: 0, lg: 8 },
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            gridRowGap: { xs: 8, lg: 0 },
          }}
        >
          <Autocomplete
            fullWidth
            getOptionLabel={R.prop('name')}
            isOptionEqualToValue={R.eqProps('id')}
            loading={isFetchingStates}
            onChange={handleChange}
            options={R.prepend(emptyState, statesQuery)}
            renderInput={renderInput({
              label: 'State',
              length: statesQuery.length,
              loading: isFetchingStates,
              required: true,
            })}
            renderTags={renderTags}
            value={destinationState}
          />
          <TextField
            fullWidth
            label="Destination Name"
            name="destinationName"
            onChange={e => {
              setDestinationName(e.target.value);
            }}
            required
            value={destinationName}
            variant="outlined"
          />
        </Box>
      </Box>
      {!disabled && (
        <Stack mt={2} spacing={2}>
          <Typography>{message}</Typography>
          {destinationsQuery.map(destination => (
            <SearchResult
              destination={destination}
              idName={idName}
              key={destination.destinationIdName}
              setDestinationName={setDestinationName}
              setDestinationState={setDestinationState}
            />
          ))}
        </Stack>
      )}
    </>
  );
};

Destination.propTypes = {
  idName: oneOfType([string, number]).isRequired,
};

export default Destination;
